// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
// @greenlight-spec: decisions/0027-integration-grant-approval-policy-placement.md
import * as React from 'react';
import { ChevronDown, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

import { AgentTestPane } from '@/components/agents/AgentTestPane';
import { CredentialAccessLedger } from '@/components/integrations/CredentialAccessLedger';
import type { CredentialVM } from '@/components/greenlight/integrationPresenter';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import { extractApiError } from './catalogRegistration';
import {
  CredentialTestResult,
  LastCredentialTestBadge,
  type CredentialTestResultBody,
} from './CredentialTestResult';
import { SignInTestButton, SignInTestResult, type SignInTests } from './SignInTest';

/**
 * Closed-by-default disclosure for one credential's access ledger. The ledger
 * fetches grants when rendered, so keeping it collapsed also defers one query
 * per credential until someone actually looks.
 */
function AccessDisclosure({
  integrationId,
  credentialSlug,
}: {
  integrationId: string;
  credentialSlug: string;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const panelId = `credential-access-${integrationId}-${credentialSlug}`;
  return (
    <div>
      <button
        type="button"
        className="flex min-h-11 items-center gap-1.5 text-[11.5px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-8"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
      >
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
        Access granted to
      </button>
      {open && (
        <div id={panelId} className="pt-1">
          <CredentialAccessLedger integrationId={integrationId} credentialSlug={credentialSlug} />
        </div>
      )}
    </div>
  );
}

function ApprovalControl({
  credential,
  onSetApproval,
  idSuffix,
}: {
  credential: CredentialVM;
  onSetApproval: (slug: string, mode: 'auto' | 'manual') => void;
  idSuffix: string;
}): React.JSX.Element {
  const auto = credential.approvalMode === 'auto';
  return (
    <span className="inline-flex items-center gap-2">
      <Switch
        id={`approval-${credential.slug}-${idSuffix}`}
        checked={auto}
        onCheckedChange={(checked) => onSetApproval(credential.slug, checked ? 'auto' : 'manual')}
        aria-label={`Auto-approve grants for ${credential.slug}`}
      />
      <span className="text-[12px] text-muted-foreground">
        {auto ? 'Auto-approve' : 'Require review'}
      </span>
    </span>
  );
}

function GrantsSection({
  integrationId,
  credentials,
  onSetApproval,
  onEditCredential,
  onDeleteCredential,
  onTested,
  testResults,
  signInTest,
  agentTest,
}: {
  integrationId: string;
  agentTest?: AgentTestProps;
  credentials: CredentialVM[];
  onSetApproval?: (slug: string, mode: 'auto' | 'manual') => void;
  onEditCredential?: (slug: string) => void;
  onDeleteCredential?: (slug: string) => void;
  onTested?: (slug: string, result: CredentialTestResultBody) => void;
  testResults?: Record<string, CredentialTestResultBody>;
  signInTest?: SignInTestProps;
}): React.JSX.Element {
  const testCredential = api.integrations.testCredential.useMutation();
  const signInTarget = (slug: string) =>
    signInTest && {
      integrationId,
      credentialSlug: slug,
      catalogKey: signInTest.catalogKey,
      authorizeUrl: signInTest.authorizeUrl,
    };
  const [testing, setTesting] = React.useState<ReadonlySet<string>>(() => new Set());
  const [agentTestSlug, setAgentTestSlug] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<Record<string, CredentialTestResultBody>>({});
  // A parent that holds results owns them outright, so a result it drops (a
  // replaced secret) is not brought back by this card's own copy.
  const shownResult = (slug: string): CredentialTestResultBody | undefined =>
    testResults ? testResults[slug] : results[slug];
  // Each row's latest run; a reply from an earlier run of the same row is dropped.
  const latestRun = React.useRef<Record<string, number>>({});

  async function runTest(slug: string): Promise<void> {
    const run = (latestRun.current[slug] ?? 0) + 1;
    latestRun.current[slug] = run;
    const isLatest = (): boolean => latestRun.current[slug] === run;
    setTesting((prev) => new Set(prev).add(slug));
    try {
      const res = await testCredential.mutateAsync({
        params: { id: integrationId, slug },
        body: {},
      });
      if (res.status !== 200) throw res;
      if (!isLatest()) return;
      setResults((prev) => ({ ...prev, [slug]: res.body }));
      onTested?.(slug, res.body);
    } catch (err) {
      if (isLatest()) {
        toast.error(extractApiError(err)?.message ?? 'The credential test failed to run.');
      }
    } finally {
      if (isLatest()) {
        setTesting((prev) => {
          const next = new Set(prev);
          next.delete(slug);
          return next;
        });
      }
    }
  }

  return (
    <div className="border-t border-border">
      <div
        data-testid={`integration-grants-${integrationId}`}
        className="divide-y divide-border bg-muted/10 px-4"
      >
        {credentials.map((credential) => (
          <section
            key={credential.slug}
            className="space-y-3 py-4"
            data-testid={`grant-details-${credential.slug}`}
          >
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(7rem,auto)_minmax(12rem,1fr)_auto_auto] lg:items-center">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <MonoValue>{credential.slug}</MonoValue>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {credential.appsLabel}
                </span>
                {!credential.configured && (
                  <span className="text-[11px] text-muted-foreground">
                    {credential.notConfiguredLabel}
                  </span>
                )}
                <LastCredentialTestBadge lastTest={credential.lastTest} />
              </div>
              <p
                className="min-w-0 truncate text-[12.5px] text-muted-foreground"
                title={credential.scope}
              >
                {credential.scope}
              </p>
              {credential.configured && onSetApproval && (
                <ApprovalControl
                  credential={credential}
                  onSetApproval={onSetApproval}
                  idSuffix="grants"
                />
              )}
              {credential.configured &&
                (onEditCredential || onDeleteCredential || onTested || signInTest) && (
                  <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                    {signInTest && (
                      <SignInTestButton
                        target={signInTarget(credential.slug)!}
                        state={signInTest.tests.stateOf(signInTarget(credential.slug)!)}
                        onStart={() => signInTest.tests.start(signInTarget(credential.slug)!)}
                      />
                    )}
                    {onTested && !signInTest && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={testing.has(credential.slug)}
                        onClick={() => void runTest(credential.slug)}
                        aria-label={`Test credential ${credential.slug}`}
                        data-testid={`test-credential-${credential.slug}`}
                      >
                        {testing.has(credential.slug) ? 'Testing…' : 'Test'}
                      </Button>
                    )}
                    {agentTest && onTested && !signInTest && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={agentTestSlug === credential.slug}
                        onClick={() => setAgentTestSlug(credential.slug)}
                        data-testid={`test-with-greenlight-agent-${credential.slug}`}
                      >
                        <FlaskConical aria-hidden /> Test with Greenlight Agent
                      </Button>
                    )}
                    {onEditCredential && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditCredential(credential.slug)}
                        aria-label={`Edit credential ${credential.slug}`}
                      >
                        Edit
                      </Button>
                    )}
                    {onDeleteCredential && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDeleteCredential(credential.slug)}
                        aria-label={`Delete credential ${credential.slug}`}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                )}
            </div>
            {shownResult(credential.slug) && (
              <CredentialTestResult result={shownResult(credential.slug)!} />
            )}
            {agentTest && agentTestSlug === credential.slug && (
              <AgentTestPane
                integrationName={agentTest.integrationName}
                baseUrl={agentTest.baseUrl}
                credentialSlug={credential.slug}
                onClose={() => setAgentTestSlug(null)}
              />
            )}
            {signInTest && (
              <SignInTestResult
                target={signInTarget(credential.slug)!}
                state={signInTest.tests.stateOf(signInTarget(credential.slug)!)}
              />
            )}
            <AccessDisclosure integrationId={integrationId} credentialSlug={credential.slug} />
          </section>
        ))}
      </div>
    </div>
  );
}

/** A per-user OAuth integration's rows offer Test sign-in in place of Test. */
export interface SignInTestProps {
  catalogKey: string | null;
  authorizeUrl: string | null;
  /** Held by the page, so a test's state survives the list refresh that remounts this card. */
  tests: SignInTests;
}

/** Preview: the Greenlight Agents Integration tester on each credential row. */
export interface AgentTestProps {
  integrationName: string;
  baseUrl: string;
}

export interface CredentialsTableProps {
  credentials: CredentialVM[];
  integrationId: string;
  onEditCredential?: (slug: string) => void;
  onDeleteCredential?: (slug: string) => void;
  onSetApproval?: (slug: string, mode: 'auto' | 'manual') => void;
  /** Shows each credential's Test action; called with each result so the parent can keep it across a refresh. */
  onTested?: (slug: string, result: CredentialTestResultBody) => void;
  /** Results the parent kept, shown under each row. */
  testResults?: Record<string, CredentialTestResultBody>;
  signInTest?: SignInTestProps;
  agentTest?: AgentTestProps;
}

export function CredentialsTable({
  credentials,
  integrationId,
  onEditCredential,
  onDeleteCredential,
  onSetApproval,
  onTested,
  testResults,
  signInTest,
  agentTest,
}: CredentialsTableProps): React.JSX.Element {
  return (
    <GrantsSection
      integrationId={integrationId}
      credentials={credentials}
      onSetApproval={onSetApproval}
      onEditCredential={onEditCredential}
      onDeleteCredential={onDeleteCredential}
      onTested={onTested}
      testResults={testResults}
      signInTest={signInTest}
      agentTest={agentTest}
    />
  );
}
